import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NgxSpinnerService } from 'ngx-spinner';
import { AdvancedDataSource } from 'src/app/controls/advanced-gridview/datasources/advanced-datasource';
import { AdvancedGridviewModel } from 'src/app/controls/advanced-gridview/models/advanced-gridview-model';
import { ColumnDefinition } from 'src/app/controls/advanced-gridview/models/column-definition';
import { ColumnType } from 'src/app/controls/advanced-gridview/models/column-type';
import { Context } from 'src/app/controls/advanced-gridview/models/context';
import { MenuITemAction } from 'src/app/controls/advanced-gridview/models/menu-item-action';
import { MenuItemEvent } from 'src/app/controls/advanced-gridview/models/menu-item-event';
import { SortInfos } from 'src/app/controls/advanced-gridview/models/sort-infos';
import { ConfirmDialogComponent } from 'src/app/controls/confirm-dialog/confirm-dialog.component';
import { FilterType } from 'src/app/controls/filters/models/filter-type';
import { DefaultListComponent } from 'src/app/controls/list/default-list/default-list.component';
import { Rights } from 'src/app/models/enums/rights';
import { Organization } from 'src/app/models/organization';
import { MemoryService } from 'src/app/services/memory.service';
import { NotificationService } from 'src/app/services/notification-service';
import { OrganizationsService } from 'src/app/services/organizations.service';
import { AdvancedGridviewComponent } from '../../../controls/advanced-gridview/advanced-gridview.component';

@Component({
    selector: 'app-organizations-list',
    templateUrl: './organizations-list.component.html',
    styleUrls: ['./organizations-list.component.scss'],
    imports: [AdvancedGridviewComponent]
})
export class OrganizationsListComponent extends DefaultListComponent<Organization> implements OnInit {
    /**
     * Configuration des colonnes de la liste.
     */
    tableConfig: ColumnDefinition[] = null;

    /**
     * Emis lorsque l'utilisateur désactive un élément.
     */
    @Output()
    itemDesactivated: EventEmitter<Organization> = new EventEmitter();

    /**
     * Emis lorsque l'utilisateur clique sur le menu contextuel 'Activer l'archivage' d'une organisation.
     */
    @Output()
    archivingActivated: EventEmitter<Organization> = new EventEmitter();

    /**
     * Constructeur
     * @param dialog : Service pour ouvrir des boites de dialogue.
     * @param memoryService : Service pour récupérer les informations en mémoire.
     * @param organizationsService Service de gestion des organisations.
     */
    constructor(
        public override dialog: MatDialog,
        protected override memoryService: MemoryService,
        private organizationsService: OrganizationsService,
        private spinner: NgxSpinnerService,
        private notificationService: NotificationService
    ) {
        super(dialog, memoryService);

        // Specifie des colonnes en fonction des droits utilisateurs
        if (this.memoryService.getConnectedUser().params.idVisible === true) {
            this.tableConfig = [
                new ColumnDefinition('id', 'Id', ColumnType.Numeric, FilterType.Text, 1, 15, true),
                new ColumnDefinition('name', 'Nom', ColumnType.Text, FilterType.Text, 2, 20, true),
                new ColumnDefinition('billingCode', 'Code facturation', ColumnType.Text, FilterType.Text, 3, 20, true),
                new ColumnDefinition('creationDate', 'Date de création', ColumnType.Date, FilterType.Date, 4, 15, true),
                new ColumnDefinition('updateDate', 'Dernière mise à jour', ColumnType.Date, FilterType.Date, 5, 15, true),
                new ColumnDefinition('archivingEnabled', 'Archivage', ColumnType.Boolean, FilterType.Boolean, 6, 10, true),
            ];
        } else {
            this.tableConfig = [
                new ColumnDefinition('name', 'Nom', ColumnType.Text, FilterType.Text, 1, 35, true),
                new ColumnDefinition('billingCode', 'Code facturation', ColumnType.Text, FilterType.Text, 2, 20, true),
                new ColumnDefinition('creationDate', 'Date de création', ColumnType.Date, FilterType.Date, 3, 15, true),
                new ColumnDefinition('updateDate', 'Dernière mise à jour', ColumnType.Date, FilterType.Date, 4, 15, true),
                new ColumnDefinition('archivingEnabled', 'Archivage', ColumnType.Boolean, FilterType.Boolean, 5, 10, true),
            ];
        }

        // Spécifie les éléments du menu contextuel en fonction des droits utilisateur
        this.menuItems = [];
        this.AddMenuItem('Consulter', MenuITemAction.Read, Rights.OrganizationRead);
        this.AddMenuItem('Éditer', MenuITemAction.Update, Rights.OrganizationEdit);
        this.AddMenuItem('Désactiver', MenuITemAction.Desactivate, Rights.OrganizationDelete);
        this.AddMenuItem('Supprimer', MenuITemAction.Delete, Rights.OrganizationDelete);
        this.AddMenuItem("Activer l'archivage", MenuITemAction.ActivateArchiving, Rights.Archiving, this.isArchivingAuthorized);
    }

    /**
     * Initialise le composant.
     */
    ngOnInit(): void {
        let context: Context = new Context('OrganizationId', null);

        if (this.memoryService.isOrganizationSelected() === true) {
            context.value = this.memoryService.getSelectedOrganization().id;
        } else {
            context.value = this.memoryService.getConnectedUser().organization.id;
        }

        this.dataSource = new AdvancedDataSource(context, this.organizationsService, this.spinner, this.notificationService);
        this.filteredTableModel = new AdvancedGridviewModel(this.tableConfig, new SortInfos('name', 'asc'));
    }

    /**
     * Appellé lorsque l'utilisateur clique sur un élément du menu contextuel.
     * @param event : Événement retourné par le menu contextuel.
     */
    public onAction(event: MenuItemEvent<Organization>): void {
        if (event.action == MenuITemAction.Read) {
            // Cas de la consultation.
            this.itemDisplayed.emit(event.context);
        } else if (event.action == MenuITemAction.Update) {
            // Cas de la mise à jour.
            this.itemEdited.emit(event.context);
        } else if (event.action == MenuITemAction.Desactivate) {
            // Cas de la mise à jour.
            this.itemDesactivated.emit(event.context);
        } else if (event.action == MenuITemAction.Delete) {
            // Cas de la suppression --> affichage de la boite de dialogue de confirmation.
            this.displayDeleteConfirmationDialog(
                `Vous êtes sur le point de supprimer l'organisation '${event.context.name}'.\nCette action supprimera tout le paramétrage Hub4B de manière définitive.`,
                event.context
            );
        } else if (event.action == MenuITemAction.ActivateArchiving) {
            // Cas de l'activation de l'archivage --> affichage de la boite de dialogue de confirmation.
            this.displayArchivingActivationConfirmationDialog(`Vous êtes sur le point d'activer l'archivage de l'organisation' '${event.context.name}'.`, event.context);
        }
    }

    /**
     * Affiche la boite de dialogue de confirmation de l'activation de l'archivage.
     * @param confirmationText : Texte de confirmation.
     * @param organization : Instance de l'organisation pour laquelle activer l'archive.
     */
    private displayArchivingActivationConfirmationDialog(confirmationText: string, organization: Organization) {
        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            maxWidth: '400px',
            data: {
                title: "Confirmation d'activation de l'archivage",
                message: confirmationText,
            },
            panelClass: 'empty-dialog',
        });

        // Attente de la réponse de la boite de dialogue de confirmation
        dialogRef.afterClosed().subscribe((dialogResult) => {
            if (dialogResult) {
                // Activation de l'archivage.
                this.archivingActivated.emit(organization);
            }
        });
    }

    private isArchivingAuthorized: (item: Organization) => boolean = (item: Organization) => {
        return item.archivingEnabled === false && this.memoryService.getSelectedOrganization().archivingEnabled === true;
    };
}
